import React from "react"

import { StringValueYesNoChip } from "@/metrics/metric-display"
import { CustomAudioSentimentConfig } from "@/metrics/registry/configuration"
import { Badge } from "@coval/ui"

import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import { SingleBinary } from "../../components/single/SingleBinary"
import { AudioSentimentMetricRecord } from "../categorical/AudioSentimentMetricRecord"

export class CustomAudioSentimentMetricRecord extends AudioSentimentMetricRecord {
  get hasVersionedConfig() {
    return true
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    return <SingleBinary mo={mo} />
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return <StringValueYesNoChip mo={mo} />
  }

  getOverviewNode(mo: RunMetricOutput[]): React.ReactNode {
    return (
      <AggregateOverviewString
        mos={mo}
        isBinary
        targetCondition={this.metric_metadata?.target_condition}
      />
    )
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    return (
      <div className="w-full">
        <AggregateOverviewString
          mos={metricOutputs}
          isBinary
          targetCondition={this.metric_metadata?.target_condition}
          onBarClick={context?.onBarClick}
          activeFilters={context?.activeFilters}
        />
      </div>
    )
  }

  _renderDeepDive(
    mo: RunMetricOutput,
    testCase: Pick<
      TestCaseRecord,
      "input_str" | "expected_behaviors" | "expected_output_json"
    >,
  ): React.ReactNode {
    const sentiments = mo.subvalues_by_timestamp ?? []
    const successSentiments = this.metric_metadata?.success_sentiments ?? []
    const percentAbove = this.metric_metadata?.percent_above ?? 50
    const role = this.metric_metadata?.role ?? "agent"

    const totalSegments = sentiments.length
    const sentimentCounts: Record<string, number> = {}
    for (const sentiment of sentiments) {
      const label = sentiment.string_value || "Unknown"
      sentimentCounts[label] = (sentimentCounts[label] ?? 0) + 1
    }

    const matchingCount = sentiments.filter((s) =>
      (successSentiments as string[]).includes(s.string_value || ""),
    ).length
    const actualPercent =
      totalSegments > 0 ? (matchingCount / totalSegments) * 100 : 0
    const passed = actualPercent >= percentAbove

    // Render the parent's deep dive (sentiment segment timeline) and prepend our summary
    const parentDeepDive = super._renderDeepDive(mo, testCase)

    return (
      <div className="space-y-6">
        {/* Pass/fail summary with sentiment breakdown */}
        <div className="border border-coval-border rounded-lg p-4 bg-surface-0">
          <div className="flex items-center gap-3 mb-3">
            <Badge variant={passed ? "success" : "danger"}>
              {passed ? "YES" : "NO"}
            </Badge>
            <span className="text-sm text-coval-text-muted">
              {matchingCount}/{totalSegments} {role} segments (
              {actualPercent.toFixed(1)}%) matched success sentiments.
              Threshold: {percentAbove}%
            </span>
          </div>

          {/* Per-sentiment percentages */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(sentimentCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([label, count]) => {
                const percent =
                  totalSegments > 0
                    ? ((count / totalSegments) * 100).toFixed(1)
                    : "0"
                const isSuccess = (successSentiments as string[]).includes(
                  label,
                )
                return (
                  <span
                    key={label}
                    className={`text-xs ${isSuccess ? "font-semibold" : "text-coval-text-muted"}`}
                  >
                    {label}: {count} ({percent}%)
                    {isSuccess ? " *" : ""}
                  </span>
                )
              })}
          </div>
        </div>

        {/* Inherited sentiment segment timeline from AudioSentimentMetricRecord */}
        {parentDeepDive}
      </div>
    )
  }

  isMetricOutputCurrentVersion(_metricOutput: {
    result_json?: Record<string, unknown> | null
  }): boolean {
    return true
  }

  getVersionDiff(_metricOutput: {
    result_json?: Record<string, unknown> | null
  }): Array<{ field: string; current: string; previous: string }> {
    return []
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <CustomAudioSentimentConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(metricRecordData, onChange, textAreaRef)}
      </div>
    )
  }
}
